<template>
  <div class="purchase-form">
    <div
      v-if="mode === 'create'"
      class="d-flex align-items-center mb-2"
      :class="mode === 'create' ? 'justify-content-between' : 'justify-content-end'"
    >
      <h6 v-if="mode === 'create'" class="mb-0">
        Nouvel achat (brouillon)
      </h6>
      <div>
        <span class="small me-2 text-muted">Statut :</span>
        <span class="badge" :class="statusClass(form.status)">
          {{ statusLabel }}
        </span>
      </div>
    </div>

    <div v-if="mode === 'create'" class="row g-3 mb-3">
      <div class="col-md-6">
        <div class="d-flex align-items-center justify-content-between">
          <label class="form-label small fw-semibold mb-1">
            Fournisseur *
          </label>

          <div class="btn-group btn-group-sm" role="group">
            <button
              type="button"
              class="btn"
              :class="supplierMode === 'new' ? 'btn-primary' : 'btn-outline-primary'"
              @click="setSupplierMode('new')"
            >
              Nouveau
            </button>
            <button
              type="button"
              class="btn"
              :class="supplierMode === 'existing' ? 'btn-primary' : 'btn-outline-primary'"
              @click="setSupplierMode('existing')"
            >
              Existant
            </button>
          </div>
        </div>

        <div v-if="supplierMode === 'new'">
          <input
            type="text"
            class="form-control form-control-sm"
            v-model="form.supplier_name"
            placeholder="Nom du fournisseur"
            required
          />
          <small class="text-muted">
            Le fournisseur sera enregistré par son nom dans l’achat. (supplier_id restera vide)
          </small>
        </div>

        <div v-else class="mt-1">
          <PrimevueDropdown
            v-model="form.supplier_id"
            :options="suppliers"
            optionLabel="label"
            optionValue="id"
            class="w-100 p-inputtext-sm"
            placeholder="Sélectionner un fournisseur"
            :loading="isLoadingSuppliers"
            filter
            filterPlaceholder="Rechercher..."
            @change="onSupplierDropdownChange"
          />
          <small class="text-muted">
            Sélectionnez un fournisseur existant (liaison forte via supplier_id).
          </small>
        </div>
      </div>

      <div class="col-md-3">
        <label class="form-label small fw-semibold">Référence commande</label>
        <input
          type="text"
          class="form-control form-control-sm"
          v-model="form.reference"
          placeholder="Laisser vide pour auto-généré"
        />
      </div>

      <div class="col-md-3">
        <label class="form-label small fw-semibold">Date d'achat *</label>
        <input
          type="date"
          class="form-control form-control-sm"
          v-model="form.purchase_date"
          required
        />
      </div>
    </div>

    <div class="card border-0 shadow-sm mb-3 purchase-comment-card">
      <div class="card-body">
        <label for="purchase-comment" class="form-label small fw-semibold">Commentaire</label>
        <textarea
          id="purchase-comment"
          rows="3"
          class="form-control form-control-sm"
          v-model="form.comment"
          placeholder="Commentaire sur cet achat (facultatif)"
        />
      </div>
    </div>

    <div class="purchase-lines-panel">
      <div class="purchase-lines-toolbar d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h6 class="mb-1">Lignes d'achat</h6>
          <div class="small text-muted">
            {{ form.items.length }} ligne(s)
          </div>
        </div>

        <div class="d-flex flex-wrap align-items-center justify-content-end gap-2">
          <div class="purchase-total-pill">
            <span class="text-muted">Total</span>
            <strong>{{ $format.currency(form.total_amount || 0) }}</strong>
          </div>

          <button
            type="button"
            class="btn btn-outline-primary btn-sm btn-icon-label"
            data-test="find-products-button"
            @click="openProductSearchDialog"
          >
            <i class="fa fa-search"></i>
            <span class="btn-label ms-1">Trouver les produits</span>
          </button>
        </div>
      </div>

      <div v-if="syncErrorMessage" class="alert alert-warning py-2 mb-3" data-test="purchase-sync-error">
        {{ syncErrorMessage }}
      </div>

      <div class="purchase-lines-datatable" data-test="purchase-lines-datatable">
        <BootstrapDataTable
          :rows="form.items"
          :columns="lineColumns"
          row-key="_uid"
          empty-text="Aucune ligne d'achat."
          :client-pagination="false"
          :show-refresh="false"
          :show-export="false"
          :show-print="false"
          :show-footer="false"
        >
          <template #cell-product_name="{ row }">
            <div class="d-flex flex-column">
              <span class="fw-semibold">{{ row.product_name }}</span>
              <small class="text-muted">{{ row.product_sku || '-' }}</small>
            </div>
          </template>

          <template #cell-quantity="{ row }">
            <input
              type="number"
              min="1"
              class="form-control form-control-sm text-end purchase-line-input"
              v-model.number="row.quantity"
              @input="handleLineChanged"
            />
          </template>

          <template #cell-unit_price="{ row }">
            <input
              type="number"
              min="0"
              step="0.01"
              class="form-control form-control-sm text-end purchase-line-input"
              v-model.number="row.unit_price"
              @input="handleLineChanged"
            />
          </template>

          <template #cell-lot_number="{ row }">
            <input
              type="text"
              class="form-control form-control-sm purchase-line-lot"
              v-model="row.lot_number"
              placeholder="N° lot"
              @input="handleLineChanged"
            />
          </template>

          <template #cell-expiry_date="{ row }">
            <input
              type="date"
              class="form-control form-control-sm purchase-line-expiry"
              v-model="row.expiry_date"
              @change="handleLineChanged"
            />
          </template>

          <template #cell-line_total="{ row }">
            <span class="fw-semibold">
              {{ $format.currency(lineTotal(row)) }}
            </span>
          </template>

          <template #cell-actions="{ row }">
            <button
              type="button"
              class="btn btn-sm btn-outline-danger btn-action-icon"
              @click.stop="removeLineByUid(row._uid)"
              title="Supprimer"
              aria-label="Supprimer la ligne"
            >
              <i class="fa fa-trash"></i>
            </button>
          </template>
        </BootstrapDataTable>
      </div>
    </div>

    <div class="d-flex justify-content-end gap-2 mt-3">
      <template v-if="mode === 'edit'">
        <button
          type="button"
          class="btn btn-outline-secondary btn-sm"
          :disabled="isActionBusy"
          @click="handleSuspend"
        >
          <i class="fa fa-pause me-1" aria-hidden="true"></i>
          Suspendre
        </button>
        <AppLoadingButton
          type="button"
          class="btn btn-success btn-sm"
          :loading="isSyncing"
          :disabled="submitting"
          loading-label="Traitement..."
          @click="handleFinish"
        >
          <i class="fa fa-check me-1"></i>
          Terminer
        </AppLoadingButton>
      </template>

      <template v-else>
        <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="submitting" @click="$emit('cancel')">
        <i class="fa fa-xmark me-1" aria-hidden="true"></i>
        Annuler
        </button>
        <AppLoadingButton
          type="button"
          class="btn btn-primary btn-sm"
          :loading="submitting"
          loading-label="Enregistrement..."
          @click="submit"
        >
          <i class="fa fa-save me-1"></i>
          Enregistrer
        </AppLoadingButton>
      </template>
    </div>

    <PrimevueDialog
      v-model:visible="productSearchDialogVisible"
      :modal="true"
      :style="{ width: '50vw', maxWidth: '95vw' }"
      :breakpoints="{ '768px': '95vw' }"
    >
      <template #header>
        <h5 class="mb-0">Trouver les produits</h5>
      </template>

      <ProductSearchPicker
        :selected-product-ids="form.items.map((item) => item.product_id)"
        :supplier-id="selectedSupplierIdForPrices"
        :show-price="true"
        :show-stock="true"
        :show-stock-filters="true"
        @add="handleProductAdd"
      />
    </PrimevueDialog>

    <div v-if="isSyncing" class="purchase-autosave-indicator" data-test="purchase-autosave-indicator">
      <i class="fa fa-spinner fa-spin"></i>
      <span>sauvegarde en cours..</span>
    </div>
  </div>
</template>

<script>
import ProductSearchPicker from '@/components/ProductSearchPicker.vue';

export default {
  name: 'PurchaseForm',
  components: {
    ProductSearchPicker,
  },
  props: {
    mode: { type: String, required: true },
    purchase: { type: Object, default: () => ({}) },
    submitting: { type: Boolean, default: false },
  },
  emits: ['save', 'cancel', 'suspend', 'finish'],
  data() {
    return {
      form: {
        id: undefined,
        reference: null,
        supplier_name: '',
        supplier_id: null,
        purchase_date: '',
        status: 'draft',
        comment: '',
        total_amount: 0,
        items: [],
      },

      supplierMode: 'new',
      suppliers: [],
      isLoadingSuppliers: false,
      productSearchDialogVisible: false,
      syncTimer: null,
      syncPromise: null,
      isSyncing: false,
      syncErrorMessage: '',
    };
  },
  computed: {
    statusLabel() {
      return this.form.status === 'draft' ? 'Brouillon' : 'Validé';
    },
    selectedSupplierIdForPrices() {
      return this.supplierMode === 'existing' && this.form.supplier_id ? this.form.supplier_id : null;
    },
    lineColumns() {
      return [
        { key: 'product_name', label: 'Produit', style: 'width: 28%;' },
        { key: 'quantity', label: 'Qté *', class: 'text-end', headerClass: 'text-end', style: 'width: 110px;' },
        { key: 'unit_price', label: 'PU achat *', class: 'text-end', headerClass: 'text-end', style: 'width: 140px;' },
        { key: 'lot_number', label: 'Lot', style: 'width: 160px;' },
        { key: 'expiry_date', label: 'Expiration', style: 'width: 150px;' },
        { key: 'line_total', label: 'Total', class: 'text-end', headerClass: 'text-end', style: 'width: 140px;' },
        { key: 'actions', label: '', class: 'text-end', headerClass: 'text-end', style: 'width: 72px;' },
      ];
    },
    canSyncLines() {
      return this.mode === 'edit' && Boolean(this.form.id);
    },
    isActionBusy() {
      return this.isSyncing || this.submitting;
    },
    localDraftStorageKey() {
      if (!this.canSyncLines) return null;

      return `suite241-gestion-commerciale:purchase-lines:${this.form.id}`;
    },
  },
  mounted() {
    this.initForm();
    this.restoreLocalDraft();
    this.fetchSuppliersIfNeeded();
  },
  beforeUnmount() {
    this.clearLineSyncTimer();
  },
  watch: {
    purchase() {
      this.clearLineSyncTimer();
      this.initForm();
      this.restoreLocalDraft();
    },
  },
  methods: {
    statusClass(status) {
      return status === 'validated' ? 'bg-success' : 'bg-secondary';
    },

    setSupplierMode(mode) {
      this.supplierMode = mode;

      if (mode === 'new') {
        this.form.supplier_id = null;
      } else {
        this.fetchSuppliersIfNeeded();
      }
    },

    initForm() {
      if (this.mode === 'edit' && this.purchase && this.purchase.id) {
        this.form.id = this.purchase.id;
        this.form.reference = this.purchase.reference || null;
        this.form.supplier_name = this.purchase.supplier_name || '';
        this.form.supplier_id =
          typeof this.purchase.supplier_id !== 'undefined' ? this.purchase.supplier_id : null;

        this.form.purchase_date = (this.purchase.purchase_date || '').slice(0, 10);
        this.form.status = this.purchase.status || 'draft';
        this.form.comment = this.purchase.comment || '';
        this.form.total_amount = this.purchase.total_amount || 0;
        this.form.items = this.normalizeItems(this.purchase.items || []);
        this.supplierMode = this.form.supplier_id ? 'existing' : 'new';
      } else {
        const today = new Date().toISOString().slice(0, 10);
        this.form.id = undefined;
        this.form.reference = null;
        this.form.purchase_date = today;
        this.form.status = 'draft';
        this.form.supplier_name = '';
        this.form.supplier_id = null;
        this.form.comment = '';
        this.form.items = [];
        this.form.total_amount = 0;
        this.supplierMode = 'new';
      }

      this.recalculateTotals();
      this.syncErrorMessage = '';
    },

    normalizeItems(items) {
      return (items || []).map((it) => ({
        _uid: it._uid || `line-${it.id || Math.random().toString(36).slice(2)}`,
        id: it.id || null,
        product_id: it.product_id,
        product_name: it.product_name,
        product_sku: it.product_sku,
        quantity: Number(it.quantity || 1),
        unit_price: Number(it.unit_price || 0),
        lot_number: it.lot_number || '',
        expiry_date: it.expiry_date ? String(it.expiry_date).slice(0, 10) : null,
      }));
    },

    async fetchSuppliersIfNeeded() {
      if (this.suppliers.length) return;

      this.isLoadingSuppliers = true;
      try {
        const res = await this.$api.get('/suppliers', {
          params: { only_active: 'true' },
        });
        const data = res.data.data || res.data || [];
        this.suppliers = data.map((s) => ({
          id: s.id,
          label: s.name,
          name: s.name,
        }));
      } catch (e) {
        console.error('Erreur chargement fournisseurs', e);
        this.suppliers = [];
      } finally {
        this.isLoadingSuppliers = false;
      }
    },

    onSupplierDropdownChange() {
      const found = this.suppliers.find((s) => s.id === this.form.supplier_id);
      if (found) {
        this.form.supplier_name = found.name;
      }
    },

    openProductSearchDialog() {
      this.productSearchDialogVisible = true;
    },

    lineUid() {
      return `line-${Math.random().toString(36).slice(2)}`;
    },

    purchasePriceFor(product) {
      if (product.supplier_purchase_price !== null && typeof product.supplier_purchase_price !== 'undefined') {
        return Number(product.supplier_purchase_price || 0);
      }

      if (product.effective_purchase_price !== null && typeof product.effective_purchase_price !== 'undefined') {
        return Number(product.effective_purchase_price || 0);
      }

      return product.purchase_price != null ? Number(product.purchase_price) : 0;
    },

    handleProductAdd({ product, quantity }) {
      if (!product) return;

      const quantityToAdd = Number(quantity || 1);
      const existingLine = this.form.items.find((item) => Number(item.product_id) === Number(product.id));

      if (existingLine) {
        return;
      } else {
        this.form.items.push({
          _uid: this.lineUid(),
          id: null,
          product_id: product.id,
          product_name: product.name,
          product_sku: product.sku,
          quantity: quantityToAdd,
          unit_price: this.purchasePriceFor(product),
          lot_number: '',
          expiry_date: null,
        });
      }

      this.handleLineChanged();
    },

    removeLineByUid(uid) {
      this.form.items = (this.form.items || []).filter((it) => it._uid !== uid);
      this.handleLineChanged();
    },

    handleLineChanged() {
      this.recalculateTotals();
      this.persistLinesLocally();
      this.queueLineSync();
    },

    recalculateTotals() {
      let total = 0;
      (this.form.items || []).forEach((it) => {
        total += this.lineTotal(it);
      });
      this.form.total_amount = total;
    },

    lineTotal(line) {
      return Number(line.quantity || 0) * Number(line.unit_price || 0);
    },

    buildItemsPayload() {
      return (this.form.items || []).map((it) => ({
        product_id: it.product_id,
        quantity: Number(it.quantity || 0),
        unit_price: Number(it.unit_price || 0),
        lot_number: (it.lot_number || '').trim() || null,
        expiry_date: it.expiry_date || null,
      }));
    },

    currentPurchasePayload() {
      return {
        ...(this.purchase || {}),
        id: this.form.id,
        reference: this.form.reference,
        supplier_name: this.form.supplier_name,
        supplier_id: this.form.supplier_id,
        purchase_date: this.form.purchase_date,
        status: this.form.status,
        comment: this.form.comment,
        total_amount: this.form.total_amount,
        items: this.form.items,
      };
    },

    hasLocalStorage() {
      return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
    },

    persistLinesLocally() {
      if (!this.localDraftStorageKey || !this.hasLocalStorage()) return;

      window.localStorage.setItem(this.localDraftStorageKey, JSON.stringify({
        purchase_id: this.form.id,
        items: this.form.items,
        total_amount: this.form.total_amount,
        updated_at: new Date().toISOString(),
      }));
    },

    restoreLocalDraft() {
      if (!this.localDraftStorageKey || !this.hasLocalStorage()) return;

      const raw = window.localStorage.getItem(this.localDraftStorageKey);
      if (!raw) return;

      try {
        const draft = JSON.parse(raw);
        if (Number(draft.purchase_id) !== Number(this.form.id) || !Array.isArray(draft.items)) return;

        this.form.items = this.normalizeItems(draft.items);
        this.recalculateTotals();
        this.syncErrorMessage = 'Des modifications locales sont en attente de synchronisation.';
        this.queueLineSync();
      } catch {
        window.localStorage.removeItem(this.localDraftStorageKey);
      }
    },

    clearLocalDraft() {
      if (!this.localDraftStorageKey || !this.hasLocalStorage()) return;

      window.localStorage.removeItem(this.localDraftStorageKey);
    },

    queueLineSync() {
      if (!this.canSyncLines) return;

      this.clearLineSyncTimer();
      this.syncTimer = window.setTimeout(() => {
        this.syncLines();
      }, 3000);
    },

    clearLineSyncTimer() {
      if (this.syncTimer) {
        window.clearTimeout(this.syncTimer);
        this.syncTimer = null;
      }
    },

    async syncLines({ force = false } = {}) {
      if (!this.canSyncLines) {
        return { success: true, purchase: this.currentPurchasePayload() };
      }

      if (force) {
        this.clearLineSyncTimer();
      }

      if (this.syncPromise) {
        await this.syncPromise.catch(() => null);
      }

      this.persistLinesLocally();

      const payloadItems = this.buildItemsPayload();
      const payloadSignature = JSON.stringify(payloadItems);
      this.isSyncing = true;
      this.syncErrorMessage = '';

      this.syncPromise = this.$api.put(`/purchases/${this.form.id}/items/sync`, {
        items: payloadItems,
      }, {
        skipErrorToast: true,
        skipSuccessToast: true,
      });

      try {
        const response = await this.syncPromise;
        const syncedPurchase = response.data?.data || null;
        const currentSignature = JSON.stringify(this.buildItemsPayload());

        if (currentSignature === payloadSignature) {
          if (syncedPurchase) {
            this.form.total_amount = Number(syncedPurchase.total_amount || 0);
            this.form.items = this.normalizeItems(syncedPurchase.items || []);
            this.form.status = syncedPurchase.status || this.form.status;
          }
          this.clearLocalDraft();
        } else {
          this.persistLinesLocally();
          this.queueLineSync();
        }

        return { success: true, purchase: syncedPurchase || this.currentPurchasePayload() };
      } catch (error) {
        console.error('Erreur synchronisation lignes achat', error);
        this.persistLinesLocally();
        this.syncErrorMessage = 'La sauvegarde automatique a échoué. Les lignes restent conservées localement.';

        return { success: false, purchase: null };
      } finally {
        this.isSyncing = false;
        this.syncPromise = null;
      }
    },

    async handleSuspend() {
      if (this.isActionBusy) return;

      const result = await this.syncLines({ force: true });
      this.$emit('suspend', result.purchase || this.currentPurchasePayload());
    },

    async handleFinish() {
      if (this.isActionBusy) return;

      if (!this.form.items.length) {
        alert('Ajoutez au moins une ligne de produit.');
        return;
      }

      const result = await this.syncLines({ force: true });
      if (!result.success) return;

      this.$emit('finish', result.purchase || this.currentPurchasePayload());
    },

    submit() {
      if (this.submitting) return;

      if (this.supplierMode === 'new') {
        if (!(this.form.supplier_name || '').trim()) {
          alert('Merci de renseigner le fournisseur.');
          return;
        }
      } else if (!this.form.supplier_id) {
        alert('Merci de sélectionner un fournisseur existant.');
        return;
      }

      if (!this.form.purchase_date) {
        alert("Merci de renseigner la date d'achat.");
        return;
      }
      if (!this.form.items.length) {
        alert('Ajoutez au moins une ligne de produit.');
        return;
      }

      const payload = {
        id: this.form.id,
        reference: this.form.reference || null,
        supplier_name: (this.form.supplier_name || '').trim(),
        supplier_id: this.supplierMode === 'existing' ? this.form.supplier_id : null,
        purchase_date: this.form.purchase_date,
        status: this.form.status,
        comment: this.form.comment,
        total_amount: this.form.total_amount,
        items: this.buildItemsPayload(),
      };

      this.$emit('save', payload);
    },
  },
};
</script>

<style scoped>
.purchase-lines-toolbar {
  min-height: 44px;
}

.purchase-total-pill {
  align-items: center;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 0.375rem;
  display: inline-flex;
  gap: 0.75rem;
  min-height: 32px;
  padding: 0.3rem 0.7rem;
}

.purchase-lines-datatable :deep(.datatable-table) {
  min-width: 920px;
}

.purchase-lines-datatable :deep(.datatable-row) {
  cursor: default;
}

.purchase-line-input {
  min-width: 88px;
}

.purchase-line-lot {
  min-width: 130px;
}

.purchase-line-expiry {
  min-width: 132px;
}

.purchase-autosave-indicator {
  align-items: center;
  background: rgba(33, 37, 41, 0.84);
  border-radius: 999px;
  bottom: 1rem;
  color: #fff;
  display: inline-flex;
  font-size: 0.78rem;
  gap: 0.45rem;
  padding: 0.4rem 0.7rem;
  position: fixed;
  right: 1rem;
  z-index: 1100;
}

@media (max-width: 575.98px) {
  .purchase-total-pill,
  .purchase-lines-toolbar .btn {
    width: 100%;
  }

  .purchase-total-pill {
    justify-content: space-between;
  }
}
</style>
